import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { formatCurrency, formatDate } from "@/lib/utils-date";
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  ResponsiveContainer,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Euro, ShoppingBag, TrendingUp, Package } from "lucide-react";
import KpiCard from "@/components/KpiCard";
import KpiDetailSheet from "@/components/KpiDetailSheet";

const CHART_COLORS = ["#2E4A3B", "#C65D47", "#D19B34", "#4A7B9D", "#3B7A57", "#8B6518", "#6B8E5A", "#A64B38"];

// Accent color used for the KPI detail trend chart, kept in sync with the card tone.
const KPI_ACCENTS = {
  revenue: "#3B7A57",
  units: "#2E4A3B",
  sales: "#4A7B9D",
  avg_ticket: "#D19B34",
};

export default function Analytics() {
  const [days, setDays] = useState("30");
  const [data, setData] = useState(null);
  const [detailMetric, setDetailMetric] = useState(null);

  useEffect(() => {
    api.salesAnalytics(Number(days)).then(setData);
  }, [days]);

  return (
    <div data-testid="analytics-page" className="space-y-8">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.12em] text-[#8E918C] mb-2">In sintesi</p>
          <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight" style={{ fontFamily: "Work Sans, sans-serif" }}>
            Analisi vendite
          </h1>
          <p className="text-[#5C5F5A] mt-2">
            Scopri cosa vende di più, cosa è fermo e come sta andando il negozio.
          </p>
        </div>
        <Tabs value={days} onValueChange={setDays} data-testid="analytics-period-tabs">
          <TabsList className="bg-[#F4F1ED] border border-[#EAE7E1]">
            <TabsTrigger value="7">7 giorni</TabsTrigger>
            <TabsTrigger value="30">30 giorni</TabsTrigger>
            <TabsTrigger value="90">90 giorni</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {/* KPI */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        <KpiCard
          testid="kpi-revenue"
          label="Incassi totali"
          value={formatCurrency(data?.total_revenue || 0)}
          icon={Euro}
          tone="success"
          subtitle="Tocca per dettaglio"
          onClick={() => setDetailMetric("revenue")}
        />
        <KpiCard
          testid="kpi-units"
          label="Unità vendute"
          value={data?.total_units ?? "—"}
          icon={Package}
          tone="neutral"
          subtitle="Tocca per dettaglio"
          onClick={() => setDetailMetric("units")}
        />
        <KpiCard
          testid="kpi-orders"
          label="N° vendite"
          value={data?.total_sales ?? "—"}
          icon={ShoppingBag}
          tone="info"
          subtitle="Tocca per dettaglio"
          onClick={() => setDetailMetric("sales")}
        />
        <KpiCard
          testid="kpi-avg"
          label="Scontrino medio"
          value={data && data.total_sales > 0 ? formatCurrency(data.total_revenue / data.total_sales) : "—"}
          icon={TrendingUp}
          tone="warning"
          subtitle="Tocca per dettaglio"
          onClick={() => setDetailMetric("avg_ticket")}
        />
      </div>

      <KpiDetailSheet
        open={!!detailMetric}
        onOpenChange={(o) => { if (!o) setDetailMetric(null); }}
        metric={detailMetric}
        days={Number(days)}
        accentColor={detailMetric ? KPI_ACCENTS[detailMetric] : "#2E4A3B"}
      />

      {/* Revenue chart */}
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mag-card p-6">
        <h3 className="text-lg font-semibold mb-6" style={{ fontFamily: "Work Sans, sans-serif" }}>
          Incassi giornalieri
        </h3>
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data?.daily || []}>
              <defs>
                <linearGradient id="rev" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#2E4A3B" stopOpacity={0.3} />
                  <stop offset="100%" stopColor="#2E4A3B" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="#EAE7E1" vertical={false} />
              <XAxis
                dataKey="date"
                stroke="#8E918C"
                fontSize={11}
                tickFormatter={(v) => {
                  const d = new Date(v);
                  return d.toLocaleDateString("it-IT", { day: "2-digit", month: "short" });
                }}
              />
              <YAxis stroke="#8E918C" fontSize={11} tickFormatter={(v) => `€${v}`} />
              <Tooltip
                contentStyle={{ background: "#FFFFFF", border: "1px solid #EAE7E1", borderRadius: 8, fontSize: 12 }}
                formatter={(v) => [formatCurrency(v), "Incasso"]}
                labelFormatter={(v) => new Date(v).toLocaleDateString("it-IT")}
              />
              <Area type="monotone" dataKey="revenue" stroke="#2E4A3B" strokeWidth={2} fill="url(#rev)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </motion.div>

      {/* Top by revenue + Category */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mag-card p-6">
          <h3 className="text-lg font-semibold mb-6" style={{ fontFamily: "Work Sans, sans-serif" }}>
            Top prodotti per incasso
          </h3>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={(data?.top_by_revenue || []).slice(0, 8)} layout="vertical" margin={{ left: 10 }}>
                <CartesianGrid stroke="#EAE7E1" horizontal={false} />
                <XAxis type="number" stroke="#8E918C" fontSize={11} tickFormatter={(v) => `€${v}`} />
                <YAxis
                  dataKey="product_name"
                  type="category"
                  stroke="#5C5F5A"
                  fontSize={11}
                  width={150}
                  tickFormatter={(v) => (v.length > 22 ? v.slice(0, 22) + "…" : v)}
                />
                <Tooltip
                  contentStyle={{ background: "#FFFFFF", border: "1px solid #EAE7E1", borderRadius: 8, fontSize: 12 }}
                  formatter={(v) => [formatCurrency(v), "Incasso"]}
                />
                <Bar dataKey="revenue" fill="#2E4A3B" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mag-card p-6">
          <h3 className="text-lg font-semibold mb-6" style={{ fontFamily: "Work Sans, sans-serif" }}>
            Ripartizione per categoria
          </h3>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={data?.by_category || []}
                  dataKey="revenue"
                  nameKey="category"
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={110}
                  paddingAngle={2}
                >
                  {(data?.by_category || []).map((_, i) => (
                    <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{ background: "#FFFFFF", border: "1px solid #EAE7E1", borderRadius: 8, fontSize: 12 }}
                  formatter={(v) => formatCurrency(v)}
                />
                <Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </motion.div>
      </div>

      {/* Stagnant + Top units */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mag-card p-6">
          <h3 className="text-lg font-semibold mb-1" style={{ fontFamily: "Work Sans, sans-serif" }}>
            I più venduti (per unità)
          </h3>
          <p className="text-sm text-[#8E918C] mb-4">Classifica prodotti nel periodo selezionato</p>
          <div className="space-y-2">
            {(data?.top_by_units || []).slice(0, 10).map((p, i) => (
              <div
                key={p.product_id}
                data-testid="top-unit-item"
                className="flex items-center justify-between p-3 rounded-lg bg-[#F9F8F6] border border-[#EAE7E1]"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <span className="text-xs font-mono font-semibold text-[#2E4A3B] bg-[#E8F3EC] border border-[#C5E1D1] h-7 w-7 rounded-md flex items-center justify-center flex-shrink-0">
                    #{i + 1}
                  </span>
                  <span className="text-sm font-medium truncate">{p.product_name}</span>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0 ml-2">
                  <span className="text-xs text-[#8E918C]">{formatCurrency(p.revenue)}</span>
                  <span className="text-sm font-semibold text-[#2E4A3B]">{p.units}</span>
                </div>
              </div>
            ))}
            {(!data?.top_by_units || data.top_by_units.length === 0) && (
              <div className="text-sm text-[#8E918C] py-8 text-center">Nessun dato</div>
            )}
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mag-card p-6">
          <h3 className="text-lg font-semibold mb-1" style={{ fontFamily: "Work Sans, sans-serif" }}>
            Prodotti fermi
          </h3>
          <p className="text-sm text-[#8E918C] mb-4">Nessuna vendita nel periodo selezionato</p>
          <div className="space-y-2 max-h-[400px] overflow-y-auto pr-1">
            {(data?.stagnant_products || []).map((p) => (
              <div
                key={p.product_id}
                data-testid="stagnant-row"
                className="flex items-center justify-between p-3 rounded-lg bg-[#F9F8F6] border border-[#EAE7E1]"
              >
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate">{p.product_name}</div>
                  <div className="text-xs text-[#8E918C] mt-0.5">
                    {p.category} · Ultima vendita: {p.last_sold_at ? formatDate(p.last_sold_at) : "mai"}
                  </div>
                </div>
                <span className="text-xs font-mono text-[#5C5F5A] bg-white border border-[#EAE7E1] px-2 py-1 rounded-md flex-shrink-0">
                  {p.stock} u.
                </span>
              </div>
            ))}
            {(!data?.stagnant_products || data.stagnant_products.length === 0) && (
              <div className="text-sm text-[#8E918C] py-8 text-center">Tutti i prodotti stanno vendendo!</div>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
}
